import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {
  Field,
  reduxForm,
  stopSubmit,
  change,
  formValueSelector
} from 'redux-form';

import FormField from 'components/FormField';
import Label from 'components/Label';
import ErrorBlock from 'components/ErrorBlock';
import Button from 'components/Button';
import {
  MoneyField,
  TextField,
  SearchField,
  DateField,
  NumberField
} from 'components/Fields';

import {formatDateForApi, getCurrentMonthFirstDay} from 'utils/datetime';
import {required, validDate} from 'utils/validators';
import {
  SCHEDULE_TYPES,
  FETCH_SCHEDULE_DELAY,
  addPayment,
  CREDIT_CARD_MACHINE,
  MODALS as TENANCY_MODALS,
  fetchCanCreateFinanceItemForPastMonths
} from 'ducks/tenancy';
import {delayedAction, composeActions} from 'ducks/common';
import {
  openModal,
  closeModal,
  selectModal,
  storeDataInModal
} from 'ducks/modals';

import './styles.css';

const ADD_PAYMENT_FOR_NON_SYNCED_INVOICE_MESSAGE = `This invoice does not have a corresponding Xero ID.
If you want the payment to be synced please add a Xero ID via the invoice options before proceeding.
Click cancel to stop this operation.`;

const ADD_PAYMENT_AND_OVERPAYMENT_FOR_NON_SYNCED_INVOICE_MESSAGE = `This invoice does not have a corresponding Xero ID.
If you want the payment and overpayment to be synced please add a Xero ID via the invoice options before proceeding.
Click cancel to stop this operation.`;

const formSubmit = (values, dispatch, props) => {
  let shouldCreate = true;
  let overpaymentDescription;
  if (Number(values.amount) > props.invoice.totalDueIncludingTax) {
    overpaymentDescription = window.prompt(
      'Enter overpayment description: ',
      ''
    );

    if (overpaymentDescription === '') {
      dispatch(stopSubmit('receivePayment'));
      return;
    }

    if (!props.invoice.xeroId) {
      shouldCreate = window.confirm(
        ADD_PAYMENT_AND_OVERPAYMENT_FOR_NON_SYNCED_INVOICE_MESSAGE
      );
      if (!shouldCreate) {
        dispatch(stopSubmit('receivePayment'));
        return;
      }
    }
  } else {
    if (!props.invoice.xeroId) {
      shouldCreate = window.confirm(ADD_PAYMENT_FOR_NON_SYNCED_INVOICE_MESSAGE);
      if (!shouldCreate) {
        dispatch(stopSubmit('receivePayment'));
        return;
      }
    }
  }

  let data: any = {
    reference: values.reference,
    amount: values.amount,
    paidTo: _.get(values, 'paidTo.id'),
    tenancyId: props.tenancyId,
    overpaymentDescription,
    invoice: props.invoice.id,
    datePaid: formatDateForApi(values.datePaid)
  };

  if (_.get(values, 'paidTo.mappedType') == CREDIT_CARD_MACHINE) {
    data = {
      ...data,
      batchNumber: values.batchNumber,
      creditCardNumber: values.creditCardNumber,
      colabAccountType: CREDIT_CARD_MACHINE
    };
  }

  const successExtraActions = props.getExtraActions();
  const delayedExtraActions = props.delayExtraActions({
    tenancyId: props.tenancyId,
    scheduleType: props.scheduleType
  });

  if (!_.isEmpty(delayedExtraActions)) {
    const delayedFetchSchedule = () =>
      delayedAction({
        delay: FETCH_SCHEDULE_DELAY,
        action: () => composeActions(delayedExtraActions)
      });
    successExtraActions.push(delayedFetchSchedule);
  }

  const failureExtraActions = [
    () => closeModal(TENANCY_MODALS.CONFIRM_PAYMENT)
  ];

  dispatch(addPayment(data, successExtraActions, failureExtraActions));
};

class ReceivePaymentForm extends React.Component<any> {
  componentDidMount() {
    const {locationRef} = this.props;
    const fetchCanCreateFinanceItemForPastMonthsExtraAction = this.props.storeDataExtraActions(
      data => ({
        canCreatePaymentForPastMonths: _.get(
          data,
          'canCreateFinanceItemsForPastMonths'
        )
      })
    );

    this.props.fetchCanCreateFinanceItemForPastMonths(
      {locationRef: locationRef},
      fetchCanCreateFinanceItemForPastMonthsExtraAction
    );
  }

  onPaidToChange = option => {
    if (_.get(option, 'mappedType') != CREDIT_CARD_MACHINE) {
      this.props.dispatch(change('receivePayment', 'batchNumber', null));
      this.props.dispatch(change('receivePayment', 'creditCardNumber', null));
    }
  };

  render() {
    const {
      handleSubmit,
      submitting,
      error,
      xeroAccounts,
      scheduleType,
      selectedAccount,
      canCreatePaymentForPastMonths
    } = this.props;

    const xeroAccountOptions = xeroAccounts.map(acc => ({
      value: `${acc.id} - ${acc.label}`,
      label: acc.label,
      id: acc.id,
      mappedType: acc.mappedType
    }));

    const creditCardMachineSelected =
      _.get(selectedAccount, 'mappedType') == CREDIT_CARD_MACHINE;

    let datepickerProps: any = undefined;
    const currentMonthFirstDay: any = getCurrentMonthFirstDay();
    if (!canCreatePaymentForPastMonths) {
      datepickerProps = {
        disabledDays: {before: new Date(currentMonthFirstDay)}
      };
    }

    const DatePickerWithPermissionsOverlay = ({
      children,
      onBlur,
      onFocus,
      tabIndex,
      month // month here is the first day of the current selected month
    }: any) => {
      const shouldShowWarning =
        !canCreatePaymentForPastMonths && moment(month) < currentMonthFirstDay;

      return (
        <div
          className="DayPickerInput-OverlayWrapper"
          onBlur={onBlur}
          onFocus={onFocus}
          tabIndex={tabIndex}>
          <div className="DayPickerInput-Overlay">
            {children}
            {shouldShowWarning && (
              <div className="colab-centered colab-bold ">
                You do not have the permission!
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="formContainer">
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <form
          onSubmit={handleSubmit}
          className={classNames('ReceivePaymentForm', {
            rent: scheduleType === SCHEDULE_TYPES.RENT,
            creditCardMachineSelected
          })}>
          <div className="fields">
            <FormField>
              <Label>Amount paid</Label>
              <Field
                name="amount"
                validate={[required]}
                component={MoneyField}
              />
            </FormField>
            <FormField>
              <Label>Date paid</Label>
              <Field
                name="datePaid"
                component={DateField}
                validate={[validDate]}
                dayPickerProps={datepickerProps}
                overlayComponent={DatePickerWithPermissionsOverlay}
              />
            </FormField>
            <FormField>
              <Label>Paid to</Label>
              <Field
                name="paidTo"
                validate={[required]}
                component={SearchField}
                {...{options: xeroAccountOptions}}
                onChange={this.onPaidToChange}
              />
            </FormField>

            <FormField>
              <Label>Reference</Label>
              <Field
                placeholder="Reference"
                name="reference"
                validate={[required]}
                component={TextField}
              />
            </FormField>

            <div className="buttonContainer">
              <Button
                onClick={() =>
                  this.props.openConfirmPaymentModal(this.props.invoice)
                }
                blue={!submitting}
                type="button"
                isLoading={submitting}
                disabled={submitting}>
                Add Payment
              </Button>
            </div>
          </div>

          {creditCardMachineSelected && (
            <div className="fields">
              <FormField>
                <Label>Batch Number</Label>
                <Field name="batchNumber" component={NumberField} />
              </FormField>

              <FormField>
                <Label>Credit card number(last 4 digits)</Label>
                <Field name="creditCardNumber" component={NumberField} />
              </FormField>
            </div>
          )}
        </form>
      </div>
    );
  }
}

const formSelector = formValueSelector('receivePayment');

const mapStateToProps = state => {
  const {data: {canCreatePaymentForPastMonths}} = selectModal(
    state,
    TENANCY_MODALS.INVOICE_DETAIL
  );

  return {
    selectedAccount: formSelector(state, 'paidTo'),
    canCreatePaymentForPastMonths
  };
};

const mapDispatchToProps = {
  openModal,
  storeDataInModal,
  fetchCanCreateFinanceItemForPastMonths
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'receivePayment',
    onSubmit: formSubmit,
    enableReinitialize: true
  })(ReceivePaymentForm)
);
